const BaseModel = require('./BaseModel');
const HTTPClient = require('../HTTPClient');

class FlowInstance extends BaseModel {
  static basePath = '/api/v1/flow-instances';
  static dataType = 'flow-instance';

  constructor(json) {
    super(json);
    this.parseToDates(['invitedAt', 'startedAt', 'completedAt', 'expiresAt']);
    this.parseChildren(['entities']);
  }

  get isPending() { return (this.state == 'pending') }
  get isQueued() { return (this.state == 'queued') }
  get isInvited() { return (this.state == 'invited') }
  get hasBeenInvited() { return (!!this.invitedAt) }
  get isStarted() { return(this.state == 'started' )}
  get hasStarted() { return (!!this.startedAt) }
  get isComplete() { return (!!this.completedAt) }
  get hasFailed() { return (this.state == 'failed') }
  get hasExpired() { return (this.state == 'expired') }
  get hasBeenAbandoned() { return (this.state == 'abandoned') }

  get canInvite() { return (!this.hasStarted && this.channelId && !this.isComplete && !this.hasExpired && !this.hasBeenAbandoned) }
  get canReset() { return (this.flowId && this.contactId && this.channelId) }
  get canDelete() { return (!this.hasStarted) }
  get canContinue() { return (this.isPending || this.isQueued || this.isInvited || this.isStarted) }

  static counts({ filters = null, group = null }) {
    let params = [`group=${group}`];
    let path = `${this.basePath}/counts`

    if (filters != null) {
      params.push(...this._nestedParamBuilder(filters, 'filter'))
    }

    if (params.length > 0) {
      path = `${path}?${params.join('&')}`;
    }

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static scores({ filters = null }) {
    let path = `${this.basePath}/scores`;
    let params = [];

    if (filters != null) {
      params.push(...this._nestedParamBuilder(filters, 'filter'))
    }

    if (params.length > 0) {
      path = `${path}?${params.join('&')}`;
    }

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static answers({ filters = null, sort = null, page = null, pageSize = null, include = null } = {}) {
    let params = [];
    let path = `${this.basePath}/answers`

    if (page != null) { params.push(`page=${page}`) }
    if (pageSize != null) { params.push(`page_size=${pageSize}`) }

    if (sort != null) {
      if (sort.constructor === Array) {
        params.push(`sort=${sort.join(',')}`);
      } else {
        params.push(`sort=${sort}`);
      }
    }

    if (filters != null) {
      params.push(...this._nestedParamBuilder(filters, 'filter'))
    }

    if (include != null) {
      if (include instanceof Array) { include = include.join(',') }
      params.push(`include=${include}`);
    }

    if (params.length > 0) {
      path = `${path}?${params.join('&')}`;
    }

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItemArray.bind(this));
  }

  static aggregates({ filters = null }) {
    const path = this.buildPathWithFilters(`${this.basePath}/answers/aggregates`, filters);

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static summarise({ filters = null }) {
    const path = this.buildPathWithFilters(`${this.basePath}/answers/summarise`, filters);

    return new HTTPClient().post(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static getSummary({ filters }) {
    const path = this.buildPathWithFilters(`${this.basePath}/answers/summary`, filters);

    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static updateSummary({ value, filters }) {
    const path = this.buildPathWithFilters(`${this.basePath}/answers/summary`, filters);

    return new HTTPClient().post(path, { answerSummary: { value } }).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static deleteSummary({ filters }) {
    const path = this.buildPathWithFilters(`${this.basePath}/answers/summary`, filters);

    return new HTTPClient().delete(path).then(this.prototype.constructor._parseDataItem.bind(this));
  }

  static buildPathWithFilters(path, filters) {
    let params = [];

    if (filters != null) {
      params.push(...this._nestedParamBuilder(filters, 'filter'))
    }

    if (params.length > 0) {
      path = `${path}?${params.join('&')}`;
    }
    return(path);
  }

  invite() {
    const path = `${this.constructor.basePath}/${this.id}/invite`;
    return new HTTPClient().post(path).then(this);
  }

  questions() {
    const path = `${this.constructor.basePath}/${this.id}/questions`;
    return new HTTPClient().get(path).then(this.prototype.constructor._parseDataItemArray.bind(this));
  }

  reset() {
    const path = `${this.constructor.basePath}/${this.id}/reset`;
    return new HTTPClient().post(path).then(this);
  }

  start() {
    const path = `${this.constructor.basePath}/${this.id}/start`;
    return new HTTPClient().post(path).then(this);
  }
}

BaseModel.models[FlowInstance.dataType] = FlowInstance;
module.exports = FlowInstance;
